import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { authMe, authLogin, authRegister, authSession, authLogout } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const processed = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          const u = await authSession(sid);
          setUser(u);
        } catch (error) {
          console.error("Google session exchange failed:", error);
        }
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setLoading(false);
      })();
    } else {
      refresh().finally(() => setLoading(false));
    }
  }, [refresh]);

  const login = async (email, password) => {
    const u = await authLogin(email, password);
    setUser(u);
    return u;
  };

  const register = async (payload) => {
    const u = await authRegister(payload);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/settings";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        refresh,
        login,
        register,
        logout,
        loginWithGoogle,
        authModalOpen,
        openAuthModal: () => setAuthModalOpen(true),
        closeAuthModal: () => setAuthModalOpen(false),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
